/**
 * Frontend-spiegel van de drie-laagse identiteit uit ADR-0020.
 *
 * Backend levert de status via `auth/me` als `meta.shadow.{mode,
 * impersonator, superadmin_origin, started_at}` — wij cachen die en
 * exposeren 'm aan banner-UI + de "Inloggen als"-knop op het
 * user-profile-scherm.
 *
 * Mutaties (start/stop) loopt via TanStack-Query-mutations (M6); deze
 * store houdt alleen de cliente kant van de identiteit + biedt twee
 * computed-equivalenten (`isShadowing`, `isUserShadow`).
 */
import { create } from 'zustand';
import { api } from '@shared/api/client';
import { useAuthStore } from '@shared/stores/auth';
import { handoffConsumeUrl } from '@shared/tenant-host';

export interface ImpersonationActor {
  id: string;
  display_name: string;
}

export type ShadowMode = 'superadmin-shadow-tenant-admin' | 'user-shadow' | null;

export interface ImpersonationMeta {
  mode: ShadowMode;
  impersonator: ImpersonationActor | null;
  superadmin_origin: ImpersonationActor | null;
  started_at: string | null;
}

const EMPTY: ImpersonationMeta = {
  mode: null,
  impersonator: null,
  superadmin_origin: null,
  started_at: null,
};

interface ImpersonationState {
  meta: ImpersonationMeta;
  loading: boolean;
  error: string | null;
  /** Hydrate vanuit een auth-me payload (na fetchMe / login). */
  hydrateFromMe: (payload: unknown) => void;
  start: (userId: string) => Promise<void>;
  openAsUser: (userId: string, appHost: string) => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

export const useImpersonationStore = create<ImpersonationState>((set) => ({
  meta: { ...EMPTY },
  loading: false,
  error: null,

  hydrateFromMe(payload) {
    const shadow = (payload as { meta?: { shadow?: Partial<ImpersonationMeta> } } | null)?.meta?.shadow;
    if (!shadow || typeof shadow !== 'object') {
      set({ meta: { ...EMPTY } });
      return;
    }
    set({
      meta: {
        mode: (shadow.mode ?? null) as ShadowMode,
        impersonator: shadow.impersonator ?? null,
        superadmin_origin: shadow.superadmin_origin ?? null,
        started_at: shadow.started_at ?? null,
      },
    });
  },

  async start(userId) {
    set({ loading: true, error: null });
    try {
      await api.post(`/api/v1/users/${userId}/impersonate`);
      // Auth-state is server-side veranderd; haal opnieuw op zodat
      // mode/impersonator/origin bijgewerkt worden.
      await useAuthStore.getState().fetchMe();
    } catch (e) {
      const anyE = e as { response?: { data?: { message?: string } } };
      const message = anyE.response?.data?.message ?? 'Failed to start shadow session';
      set({ error: message });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  /**
   * Cross-host handoff (ADR-0028): mint een eenmalige token op de manage-host
   * en open de eindgebruiker-host als shadow van {userId} in een NIEUW tabblad.
   * De admin-sessie op de manage-host blijft intact → concurrent. Dit vervangt
   * de in-sessie-swap (`start`) voor de admin-geïnitieerde flow.
   */
  async openAsUser(userId, appHost) {
    set({ loading: true, error: null });
    // Open het tabblad SYNCHROON op de klik (vóór de async mint) zodat de
    // popup-blocker 'm niet tegenhoudt; daarna zetten we de definitieve URL.
    // Geen noopener hier — anders geeft window.open null terug en kunnen we
    // de locatie niet zetten; de consume navigeert direct naar onze eigen host.
    const tab = window.open('', '_blank');
    try {
      const { data } = await api.post<{ token: string }>(`/api/v1/users/${userId}/handoff`);
      const url = handoffConsumeUrl(appHost, data.token);
      if (tab) {
        tab.location.href = url;
      } else {
        // Popup toch geblokkeerd → best-effort fallback.
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      tab?.close();
      const anyE = e as { response?: { data?: { message?: string } } };
      const message = anyE.response?.data?.message ?? 'Failed to open shadow session';
      set({ error: message });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  async stop() {
    set({ loading: true, error: null });
    try {
      await api.delete('/api/v1/impersonation');
      await useAuthStore.getState().fetchMe();
    } catch (e) {
      const anyE = e as { response?: { data?: { message?: string } } };
      const message = anyE.response?.data?.message ?? 'Failed to stop shadow session';
      set({ error: message });
      throw e;
    } finally {
      set({ loading: false });
    }
  },

  reset() {
    set({ meta: { ...EMPTY }, error: null });
  },
}));

// Selector helpers — buiten de store gehouden zodat ze niet onnodig
// `set` re-runs triggeren bij wijzigingen.
export const selectIsShadowing = (s: ImpersonationState) => s.meta.mode !== null;
export const selectIsUserShadow = (s: ImpersonationState) => s.meta.mode === 'user-shadow';
